// Spec §2.7 — TR-level config override panel embedded on the Гэрээт
// харилцагч detail page. Lets the supplier-side coordinator pin per-
// customer SLA priority, preferred warehouse/driver, channel labels,
// and carrier preferences. The buyer side sees a read-only summary
// (only fields that have been overridden).

import { useEffect, useMemo, useState } from "react"
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { CaretDownIcon, CaretUpIcon, PlusIcon, XIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryApi, NetworkApi } from "../../apis"
import type {
  INetworkConfigOverride,
  INetworkConfigOverrideInput,
  SlaPriority,
} from "../../apis/network"
import { nameOptionsFilter } from "@dehub/theme"
import { usePermission } from "../permission"
import { ListState } from "../ui"
import { renderName } from "../../utils/render-name"
import { message } from "../../utils/message"
import HttpHandler from "../../utils/http-handler"

// The stored codes stay English (BE enum); the labels come from the locale.
const SLA_CODES: SlaPriority[] = ["VIP", "URGENT", "HIGH", "NORMAL", "LOW"]

// Mirrors backend GOVERNANCE_DEFAULTS.channelLabels — used as placeholders
// when the supplier hasn't overridden a label.
const CHANNELS = [
  { key: "LOCAL_DELIVERY"  },
  { key: "SUPPLY_FREIGHT"  },
  { key: "CUSTOMER_PICKUP" },
] as const

type ChannelKey = (typeof CHANNELS)[number]["key"]

type Props = {
  networkId: string
  isSupplierSide: boolean
  // Supplier business id — drives the warehouse/driver lists. Optional:
  // when missing we just hide those pickers.
  supplierBusinessId?: string | null
}

type FormState = {
  slaPriority          : SlaPriority | null
  preferredWarehouseId : string
  preferredDriverUserId: string
  channelLabels        : Record<ChannelKey, string>
  carriers             : string[]
}

function buildFormState (data: INetworkConfigOverride | null | undefined): FormState {
  const labels    = (data?.channelLabelOverrides || {}) as Partial<Record<ChannelKey, string>>
  const preferred = (data?.carrierPreference as { preferred?: unknown } | null)?.preferred
  return {
    slaPriority          : data?.slaPriority ?? null,
    preferredWarehouseId : data?.preferredWarehouseId ?? "",
    preferredDriverUserId: data?.preferredDriverUserId ?? "",
    channelLabels        : {
      LOCAL_DELIVERY : labels.LOCAL_DELIVERY  ?? "",
      SUPPLY_FREIGHT : labels.SUPPLY_FREIGHT  ?? "",
      CUSTOMER_PICKUP: labels.CUSTOMER_PICKUP ?? "",
    },
    carriers: Array.isArray(preferred)
      ? preferred.filter((x): x is string => typeof x === "string")
      : [],
  }
}

const EMPTY_STATE: FormState = {
  slaPriority          : null,
  preferredWarehouseId : "",
  preferredDriverUserId: "",
  channelLabels        : { LOCAL_DELIVERY: "", SUPPLY_FREIGHT: "", CUSTOMER_PICKUP: "" },
  carriers             : [],
}

export function NetworkConfigOverridePanel ({ networkId, isSupplierSide, supplierBusinessId }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])

  // BE network/config-override get + put both require ORD_DN_MNG.
  const ORD_DN_MNG = usePermission("ORD_DN_MNG")
  const { data, error: loadError, isLoading, mutate } = useSWR<INetworkConfigOverride | null>(
    networkId && ORD_DN_MNG.hasAccess ? `/ord/network/${networkId}/config-override` : null,
    () => NetworkApi.getConfigOverride(networkId),
    { revalidateOnFocus: false }
  )

  const { data: warehouses } = useSWR(
    supplierBusinessId ? `/ord/order/backorder/warehouse/select:${supplierBusinessId}` : null,
    () => DeliveryApi.warehouseSelect({ businessId: supplierBusinessId!, limit: 100 }),
    { revalidateOnFocus: false }
  )
  const warehouseOptions = ((warehouses ?? []) as any[]).map((w) => ({
    value: w.id,
    label: w.name ?? w.refCode ?? w.id,
  }))

  const { data: drivers } = useSWR(
    supplierBusinessId ? `/ord/delivery_note/staff_select:${supplierBusinessId}` : null,
    () => DeliveryApi.staffSelect({ businessId: supplierBusinessId! }),
    { revalidateOnFocus: false }
  )
  const driverOptions = ((drivers ?? []) as any[]).map((d) => ({
    value: d.id,
    label: renderName(d) || d.email || d.id,
  }))

  const [initial, setInitial]           = useState<FormState>(EMPTY_STATE)
  const [form, setForm]                 = useState<FormState>(EMPTY_STATE)
  const [submitting, setSubmitting]     = useState(false)
  const [error, setError]               = useState<string | null>(null)
  const [carrierDraft, setCarrierDraft] = useState("")

  useEffect(() => {
    if (data === undefined) return
    const next = buildFormState(data)
    setInitial(next)
    setForm(next)
  }, [data])

  const dirty = useMemo(() => {
    if (form.slaPriority           !== initial.slaPriority)           return true
    if (form.preferredWarehouseId  !== initial.preferredWarehouseId)  return true
    if (form.preferredDriverUserId !== initial.preferredDriverUserId) return true
    for (const c of CHANNELS) {
      if (form.channelLabels[c.key] !== initial.channelLabels[c.key]) return true
    }
    if (form.carriers.length !== initial.carriers.length) return true
    for (let i = 0; i < form.carriers.length; i++) {
      if (form.carriers[i] !== initial.carriers[i]) return true
    }
    return false
  }, [form, initial])

  const buildPayload = (): INetworkConfigOverrideInput => {
    const labels = Object.fromEntries(
      Object.entries(form.channelLabels)
        .map(([k, v]) => [k, v.trim()] as const)
        .filter(([, v]) => v.length > 0)
    ) as Record<string, string>
    const carriers = form.carriers.map((c) => c.trim()).filter((c) => c.length > 0)
    return {
      slaPriority          : form.slaPriority,
      preferredWarehouseId : form.preferredWarehouseId.trim()  || null,
      preferredDriverUserId: form.preferredDriverUserId.trim() || null,
      channelLabelOverrides: Object.keys(labels).length > 0 ? labels : null,
      carrierPreference    : carriers.length > 0 ? { preferred: carriers } : null,
    }
  }

  const onSave = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await NetworkApi.upsertConfigOverride(networkId, buildPayload())
      await mutate()
      message.success(t("common:messages.requestSuccess"))
    } catch (err) {
      setError((err as HttpHandler)?.message ?? t("common:messages.requestFailed"))
    } finally {
      setSubmitting(false)
    }
  }

  const onReset = () => {
    setForm(initial)
    setCarrierDraft("")
    setError(null)
  }

  // ---- Carrier list helpers
  const addCarrier = () => {
    const v = carrierDraft.trim()
    if (!v) return
    if (form.carriers.includes(v)) {
      setCarrierDraft("")
      return
    }
    setForm((f) => ({ ...f, carriers: [...f.carriers, v] }))
    setCarrierDraft("")
  }
  const removeCarrier = (idx: number) =>
    setForm((f) => ({ ...f, carriers: f.carriers.filter((_, i) => i !== idx) }))
  const moveCarrier = (idx: number, dir: -1 | 1) => {
    setForm((f) => {
      const next = [...f.carriers]
      const swap = idx + dir
      if (swap < 0 || swap >= next.length) return f
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return { ...f, carriers: next }
    })
  }

  // ---- Visibility (buyer-side hides un-overridden fields)
  const slaFilled       = form.slaPriority != null
  const warehouseFilled = !!form.preferredWarehouseId
  const driverFilled    = !!form.preferredDriverUserId
  const channelFilled   = (k: ChannelKey) => form.channelLabels[k].trim().length > 0
  const anyChannelFilled = CHANNELS.some((c) => channelFilled(c.key))
  const carriersFilled   = form.carriers.length > 0

  const showPriority  = isSupplierSide || slaFilled
  const showResources = isSupplierSide || warehouseFilled || driverFilled
  const showChannels  = isSupplierSide || anyChannelFilled || carriersFilled
  const nothingOverridden =
    !isSupplierSide &&
    !slaFilled && !warehouseFilled && !driverFilled && !anyChannelFilled && !carriersFilled

  if (!ORD_DN_MNG.hasAccess) return <>{ORD_DN_MNG.accessDenied()}</>

  // A failed load must never reach the editable form: saving it would send every field as null and wipe the stored override.
  if (loadError) return <ListState state="error" onRetry={() => mutate()} errorCode={(loadError as { statusCode?: number })?.statusCode} />

  if (isLoading || data === undefined) {
    return (
      <Paper p="md" radius="md" withBorder>
        <Group justify="center" py="md"><Loader size="sm" /></Group>
      </Paper>
    )
  }

  return (
    <Paper p={20} radius="lg" withBorder>
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Box>
          <Title order={5}>
            {t("supplierSales:configOverride.title")}
          </Title>
          <Text fz={14} c="gray.8" mt={4}>
            {t("supplierSales:configOverride.subtitle", {
              defaultValue: "Тухайн харилцагчид тохирсон давуу тал — хоосон үед бизнесийн анхдагч хэрэглэгдэнэ.",
            })}
          </Text>
        </Box>
        {!isSupplierSide && (
          <Badge variant="light" color="gray" radius="sm">
            {t("supplierSales:configOverride.readOnlyBadge", { defaultValue: "Зөвхөн харах" })}
          </Badge>
        )}
      </Group>

      {error && <Alert color="red" variant="light" mb="md">{error}</Alert>}

      {nothingOverridden ? (
        <Alert color="gray" variant="light">
          {t("supplierSales:configOverride.noOverride", {
            defaultValue: "Override байхгүй — нийлүүлэгч талын анхдагч тохиргоо хэрэглэгдэж байна.",
          })}
        </Alert>
      ) : (
        <Stack gap="lg">
          {/* Ord_CustomerDetail: priority + resources on the left, channels + carriers on the right. */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Stack gap="lg">
              {showPriority && (
                <SectionBlock title={t("supplierSales:configOverride.section.priority", { defaultValue: "Приоритет" })}>
                  <Select
                    label={t("supplierSales:configOverride.slaPriority", { defaultValue: "SLA priority" })}
                    placeholder={t("supplierSales:configOverride.inheritDefault", { defaultValue: "(Анхдагч)" })}
                    data={SLA_CODES.map((value) => ({ value, label: t(`supplierSales:configOverride.sla.${value}`) }))}
                    value={form.slaPriority}
                    onChange={(v) => setForm((f) => ({ ...f, slaPriority: v as SlaPriority | null }))}
                    clearable
                    disabled={!isSupplierSide || submitting}
                  />
                </SectionBlock>
              )}

              {showResources && (
                <SectionBlock title={t("supplierSales:configOverride.section.resources", { defaultValue: "Ресурс" })}>
                  {(isSupplierSide || warehouseFilled) && (
                    <Select
                      label={t("supplierSales:configOverride.preferredWarehouse", { defaultValue: "Хүлээгдэх агуулах" })}
                      placeholder={t("supplierSales:configOverride.inheritDefault", { defaultValue: "(Анхдагч)" })}
                      data={warehouseOptions}
                      value={form.preferredWarehouseId || null}
                      onChange={(v) => setForm((f) => ({ ...f, preferredWarehouseId: v ?? "" }))}
                      searchable
                      clearable
                      disabled={!isSupplierSide || submitting || !supplierBusinessId}
                    />
                  )}
                  {(isSupplierSide || driverFilled) && (
                    <Select
                      label={t("supplierSales:configOverride.preferredDriver", { defaultValue: "Хүлээгдэх жолооч" })}
                      placeholder={t("supplierSales:configOverride.inheritDefault", { defaultValue: "(Анхдагч)" })}
                      data={driverOptions}
                      value={form.preferredDriverUserId || null}
                      onChange={(v) => setForm((f) => ({ ...f, preferredDriverUserId: v ?? "" }))}
                      searchable
                      filter={nameOptionsFilter}
                      clearable
                      disabled={!isSupplierSide || submitting || !supplierBusinessId}
                    />
                  )}
                </SectionBlock>
              )}
            </Stack>
            {showChannels && (
              <SectionBlock title={t("supplierSales:configOverride.section.channels", { defaultValue: "Сувагийн тохиргоо" })}>
                {(isSupplierSide || anyChannelFilled) && (
                  <Stack gap="xs">
                    <Text fz={14} c="gray.8">
                      {t("supplierSales:configOverride.channelLabelsHelp", {
                        defaultValue: "Сувагийн харагдах нэр (хоосон үед анхдагч хэрэглэгдэнэ)",
                      })}
                    </Text>
                    {CHANNELS.map((ch) => {
                      if (!isSupplierSide && !channelFilled(ch.key)) return null
                      return (
                        <TextInput
                          key={ch.key}
                          label={t(`configOverride.channelDefaults.${ch.key}`)}
                          placeholder={t(`configOverride.channelDefaults.${ch.key}`)}
                          value={form.channelLabels[ch.key]}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              channelLabels: { ...f.channelLabels, [ch.key]: e.currentTarget.value },
                            }))
                          }
                          disabled={!isSupplierSide || submitting}
                        />
                      )
                    })}
                  </Stack>
                )}

                {(isSupplierSide || carriersFilled) && (
                  <Stack gap="xs" mt="md">
                    <Text size="sm" fw={500}>
                      {t("supplierSales:configOverride.carrierPreference", { defaultValue: "Тээвэрлэгчид (дарааллаар)" })}
                    </Text>
                    {form.carriers.map((c, i) => (
                      <Group key={`${i}-${c}`} gap="xs" wrap="nowrap" align="center">
                        <Box style={{ flex: 1 }}>
                          <TextInput value={c} readOnly variant="filled" />
                        </Box>
                        {isSupplierSide && (
                          <>
                            <ActionIcon
                              variant="default"
                              size="lg"
                              onClick={() => moveCarrier(i, -1)}
                              disabled={i === 0 || submitting}
                              aria-label="up">
                              <CaretUpIcon size={14} />
                            </ActionIcon>
                            <ActionIcon
                              variant="default"
                              size="lg"
                              onClick={() => moveCarrier(i, 1)}
                              disabled={i === form.carriers.length - 1 || submitting}
                              aria-label="down">
                              <CaretDownIcon size={14} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="lg"
                              onClick={() => removeCarrier(i)}
                              disabled={submitting}
                              aria-label="remove">
                              <XIcon size={14} />
                            </ActionIcon>
                          </>
                        )}
                      </Group>
                    ))}
                    {isSupplierSide && (
                      <Group gap="xs" wrap="nowrap" align="flex-end">
                        <Box style={{ flex: 1 }}>
                          <TextInput
                            placeholder={t("supplierSales:configOverride.carrierAddPlaceholder", { defaultValue: "Тээвэрлэгчийн нэр" })}
                            value={carrierDraft}
                            onChange={(e) => setCarrierDraft(e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addCarrier()
                              }
                            }}
                            disabled={submitting}
                          />
                        </Box>
                        <Button
                          variant="light"
                          leftSection={<PlusIcon size={14} />}
                          onClick={addCarrier}
                          disabled={!carrierDraft.trim() || submitting}>
                          {t("common:actions.add", { defaultValue: "Нэмэх" })}
                        </Button>
                      </Group>
                    )}
                  </Stack>
                )}
              </SectionBlock>
            )}
          </SimpleGrid>

          {isSupplierSide && (
            <Group justify="flex-end" gap="xs" mt="md">
              <Button
                variant="default"
                onClick={onReset}
                disabled={!dirty || submitting}>
                {t("common:actions.cancel", { defaultValue: "Цуцлах" })}
              </Button>
              <Button
                onClick={onSave}
                loading={submitting}
                disabled={!dirty}>
                {t("common:actions.save", { defaultValue: "Хадгалах" })}
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </Paper>
  )
}

function SectionBlock ({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="sm">
      <Text fz={14} fw={700} c="gray.8" tt="uppercase">
        {title}
      </Text>
      <Stack gap="sm">{children}</Stack>
    </Stack>
  )
}
